import { Injectable } from '@angular/core';
import { PasswordRequirement } from '../gql/types';
import { PASSWORD_REQUIREMENTS_QUERY } from '../gql/queries';
import { filter, map } from 'rxjs';
import { Apollo } from 'apollo-angular';

const DEFAULT_LOCALE = 'en-US';
const ALLOWED_LOCALES = ['en', 'en-AU', 'en-GB', 'en-US', 'pt', 'pt-MZ'];

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  languagesList = [
    {
      tag: 'en-AU',
      name: 'English - Australia'
    },
    {
      tag: 'en-GB',
      name: 'English - United Kingdom'
    },
    {
      tag: 'en-US',
      name: 'English - United States'
    },
    {
      tag: 'pt-MZ',
      name: 'Portuguese - Mozambique'
    }
  ];

  constructor(private apollo: Apollo) {}

  get currentLang(): string {
    if (typeof Storage === 'undefined') {
      return DEFAULT_LOCALE;
    }

    const storedLanguage = localStorage.getItem('lang');
    if (storedLanguage && storedLanguage !== 'Select Locale...') {
      return storedLanguage;
    }

    let lang = navigator.language || navigator['userLanguage'];
    // Need to only allow languages in the `languagesList`
    if (ALLOWED_LOCALES.includes(lang)) {
      // The browser might set shorthand locales, so we'll map those to the correct ones here
      const shorthandLocaleMap = {
        en: 'en-US',
        pt: 'pt-MZ'
      };
      lang = shorthandLocaleMap[lang] || lang;
    } else {
      lang = DEFAULT_LOCALE;
    }

    this.setLanguage(lang);

    return lang;
  }

  setLanguage(lang: string) {
    if (typeof Storage !== 'undefined') {
      localStorage.setItem('lang', lang);
    }
  }

  getLanguage() {
    return this.currentLang;
  }

  registerCulture(culture: string) {
    if (!culture) {
      return;
    }
    this.setLanguage(culture);
  }

  getPasswordRequirements() {
    return this.apollo
      .query<{ passwordValidationRequirements: PasswordRequirement[] }>({
        query: PASSWORD_REQUIREMENTS_QUERY,
        context: { muteError: true }
      })
      .pipe(
        map(({ data }) => data?.passwordValidationRequirements),
        filter((requirements) => !!requirements)
      );
  }
}
